import { PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppLayout } from '../components/AppLayout';
import { PageHead } from '../components/PageHead';
import { api, type EventRow } from '../lib/api';
import { useIsSuperAdmin } from '../lib/me';

const { Title, Text } = Typography;

type EventFormValues = { title: string; date?: Dayjs };

export function EventsScene() {
  const isSuperAdmin = useIsSuperAdmin();
  const [events, setEvents] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<EventFormValues>();

  const load = async () => {
    setLoading(true);
    try {
      const rows = await api<EventRow[]>('/api/events');
      setEvents(rows);
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const onCreate = async (values: EventFormValues) => {
    setCreating(true);
    try {
      await api('/api/events', {
        method: 'POST',
        body: JSON.stringify({
          title: values.title,
          date: values.date ? values.date.format('YYYY-MM-DD') : null,
        }),
      });
      form.resetFields();
      setModalOpen(false);
      await load();
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to create event');
    } finally {
      setCreating(false);
    }
  };

  const columns: ColumnsType<EventRow> = [
    {
      title: 'Event',
      dataIndex: 'title',
      key: 'title',
      render: (title: string, row) => <Link to={`/events/${row.id}`}>{title}</Link>,
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 160,
      render: (date: string | null) => (date ? dayjs(date).format('D MMM YYYY') : '—'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: EventRow['status']) => {
        const color =
          status === 'live' ? 'green' : status === 'ended' ? 'default' : 'blue';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    ...(isSuperAdmin
      ? [
          {
            title: 'Assigned to',
            dataIndex: 'assignee_name',
            key: 'assignee_name',
            width: 200,
            render: (n: string | null) =>
              n ? <Text>{n}</Text> : <Text type="secondary">Unassigned</Text>,
          },
        ]
      : []),
  ];

  return (
    <AppLayout>
      <PageHead title="Events" />
      <Space
        align="center"
        style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}
      >
        <Title level={2} style={{ margin: 0 }}>
          Events
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
          New event
        </Button>
      </Space>

      <Card bodyStyle={{ padding: 0 }}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={events}
          loading={loading}
          pagination={false}
          locale={{ emptyText: 'No events yet — create one to get started.' }}
        />
      </Card>

      <Modal
        title="Create event"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={creating}
        okText="Create"
      >
        <Form form={form} layout="vertical" onFinish={onCreate}>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Please enter a title' }]}
          >
            <Input autoFocus />
          </Form.Item>
          <Form.Item name="date" label="Date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </AppLayout>
  );
}
